// Core packages
import { Component, Inject, OnInit } from "@angular/core";
import { FormGroup } from "@angular/forms";

// Third party packages
import { MatDialogRef, MAT_DIALOG_DATA } from "@angular/material/dialog";
import { FormlyFieldConfig, FormlyFormOptions } from "@ngx-formly/core";
import { TranslateService } from "@ngx-translate/core";
import { MatProgressButtonOptions } from "mat-progress-buttons";
import { CookieService } from "ngx-cookie-service";
import { ToastrService } from "ngx-toastr";
import { Subscription } from "rxjs";
import { finalize } from "rxjs/operators";
import { AuthService } from "src/app/modules/auth/auth.service";

// Custom packages
import { CompaniesService } from "src/app/modules/companies/companies.service";
import { IUser } from "src/app/shared/models/user/user.interface";
import { ConfigService } from "src/app/shared/services/config.service";
import { HelperService } from "src/app/shared/services/helper.service";

@Component({
  selector: "app-disclaimer-dialog",
  templateUrl: "./disclaimer-dialog.component.html",
  styleUrls: ["./disclaimer-dialog.component.scss"],
})
export class DisclaimerDialogComponent implements OnInit {
  private subscriptions: Subscription[] = [];
  loggedUser: IUser;
  loggedUserPermissions: string[];
  lang = this.translateService.currentLang || this.translateService.defaultLang;

  dialogIsLoading = false; // Used to view/hide form loading spinner

  // BEGIN - Form
  // BEGIN - ngx-formly
  form: FormGroup = new FormGroup({});
  model: any = {};
  fields: FormlyFieldConfig[] = [];
  options: FormlyFormOptions = {};
  // END - ngx-formly
  formBtnOptions: MatProgressButtonOptions = {
    active: false,
    text: this.translateService.instant("common.cta.confirm"),
    spinnerSize: 19,
    raised: true,
    stroked: true,
    flat: false,
    fab: false,
    buttonColor: "primary",
    spinnerColor: "primary",
    fullWidth: false,
    disabled: false,
    mode: "indeterminate",
    buttonIcon: {
      fontSet: "fal",
      fontIcon: "fa-save",
      inline: true,
    },
  };
  // END - Form

  constructor(
    public dialogRef: MatDialogRef<DisclaimerDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: any,
    private translateService: TranslateService,
    private authService: AuthService,
    private cookieService: CookieService,
    private configService: ConfigService,
    private toastrService: ToastrService,
    public helperService: HelperService,
    private companiesService: CompaniesService,
  ) {
    dialogRef.disableClose = true;
  }

  /**
   * Init component
   *
   * @since 1.0.0
   */
  ngOnInit() {
    // Get logged user data
    this.subscriptions.push(
      this.authService.loggedUser$.subscribe((res) => (this.loggedUser = res))
    );
    this.subscriptions.push(
      this.authService.loggedUserPermissions$.subscribe(
        (res) => (this.loggedUserPermissions = res)
      )
    );

    // Get current lang
    this.lang =
      this.cookieService.get("lang") || this.configService.defaultLang;

    // Init form
    this.initForm();
  }

  /**
   * Init form
   *
   * @since 1.0.0
   */
  initForm(): void {
    this.fields = [
      {
        fieldGroupClassName: "row",
        fieldGroup: [
          {
            className: "col-12 boxForm",
            template:
              "<div>" +
              this.translateService.instant(
                "dialogs.disclaimer.text1"
              ) +
              "</div>",
          },
          {
            className: "col-12 col-md-4 col-xl-3",
            type: "checkbox",
            key: "check1",
            defaultValue: false,
            templateOptions: {
              label: this.translateService.instant("dialogs.disclaimer.checked"),
              placeholder: this.translateService.instant(
                "dialogs.disclaimer.checked"
              ),
              // pattern: "false",
              required: true,
            },
          },
          {
            className: "col-12 boxForm",
            template:
              "<div>" +
              this.translateService.instant(
                "dialogs.disclaimer.text2"
              ) +
              "</div>",
          },
          {
            className: "col-12 col-md-4 col-xl-3",
            type: "checkbox",
            key: "check2",
            defaultValue: false,
            templateOptions: {
              label: this.translateService.instant("dialogs.disclaimer.checked"),
              placeholder: this.translateService.instant(
                "dialogs.disclaimer.checked"
              ),
              // pattern: "false",
              required: true,
            },
          },
          {
            className: "col-12 boxForm",
            template:
              "<div>" +
              this.translateService.instant(
                "dialogs.disclaimer.text3"
              ) +
              "</div>",
          },
          {
            className: "col-12 col-md-4 col-xl-3",
            type: "checkbox",
            key: "check3",
            defaultValue: false,
            templateOptions: {
              label: this.translateService.instant("dialogs.disclaimer.checked"),
              placeholder: this.translateService.instant(
                "dialogs.disclaimer.checked"
              ),
              // pattern: "false",
              required: true,
            },
          },
          {
            className: "col-12 boxForm",
            template:
              "<div>" +
              this.translateService.instant(
                "dialogs.disclaimer.text4"
              ) +
              "</div>",
          },
          {
            className: "col-12 col-md-4 col-xl-3",
            type: "checkbox",
            key: "check4",
            defaultValue: false,
            templateOptions: {
              label: this.translateService.instant("dialogs.disclaimer.checked"),
              placeholder: this.translateService.instant(
                "dialogs.disclaimer.checked"
              ),
              // pattern: "false",
              required: true,
            },
          },
        ],
      },
    ];
  }

  /**
   * Handle form submit
   *
   * @since 1.0.0
   */
  onFormSubmit(): void {
    // Prevent invalid form submit
    if (this.form.invalid) {
      Object.keys(this.form.controls).forEach((key) => {
        this.form.controls[key].markAsTouched();
      });
      console.warn("form", this.form);
      this.toastrService.error(
        this.translateService.instant("common.errors.form_is_invalid")
      );
      return;
    }

    // Activate submit button loading status
    this.formBtnOptions.active = true;

    if (this.form.value) {
      const sendData = {
        disclaimer: this.model,
      }
      this.subscriptions.push(
        this.companiesService
          .updateDisclaimer(this.data.companyId, sendData)
          .pipe(finalize(() => (this.formBtnOptions.active = false)))
          .subscribe(
            (res) => {
              if (res.status) {
                this.toastrService.success(res.message);
                this.dialogRef.close(true);
              }
            },
            (err) => {
              // Choose one of the following error handling method.
              // 1) First one show a message right under the form fields
              //    (if the form is properly setted)
              // 2) Second one is used to show a mat-error under each field
              //    in the dynamic form (is meant to be used only in dynamicForm)
              // 3) Third one show toastr notifications for each error
              // 4) Fourth one is used to show mat-error under each field
              //    in the ngx-formly form (is mean to be used only with ngx-formly)
              // this.helperService.handleFormError(form, err);
              // this.helperService.handleDynamicFormError(form, err);
              // this.helperService.handleError(err);
              this.helperService.handleFormlyError(this.form, err);
            }
          )
      );
    }
  }

  /**
   * Handle click on Denial button
   *
   * @since 1.0.0
   */
  onClose(): void {
    this.dialogRef.close(false);
  }

  /**
   * Handle click on confirmation button
   *
   * @since 1.0.0
   */
  onConfirmation(): void {
    this.dialogRef.close(true);
  }
}
